/**
 * Copyright (c) 2020 Honeywell International Inc. All Rights Reserved.
 * Honeywell and its product names are among the trademarks and/or service marks
 * owned by Honeywell International, Inc., or its subsidiaries.
 *
 * For patent information, see http://www.hsmpats.com.
 */

import React, { Component } from 'react';
import { AppContext } from '@keystone/common';
import PropTypes from 'prop-types';
import _ from 'lodash';
import APIClient from './APIClient';

export const CustomerSiteContext = React.createContext();
export const CustomerSiteConsumer = CustomerSiteContext.Consumer;

export class CustomerSiteProvider extends Component {
  constructor(props) {
    super(props);
    this.state = {
      customerList: [],
      selectedCustomer: null,
      selectedCustomerName: '',
      siteList: [],
      selectedSite: '-1',
    };
  }

  init = async () => {
    const { userData, handleError } = this.context;   
    try {
      const name = userData.customerList.find(selectedCustomerName => selectedCustomerName.value === userData.selectedCustomer);
      const orderedCustomerList = this.sortCustomerList(userData.customerList);
      this.getSiteList();
      this.setState({
        customerList: orderedCustomerList,
        selectedCustomer: userData.selectedCustomer,
        selectedCustomerName: name.text,
      });
    } catch (err) {
      handleError(err);
      console.error(err);
    }
  };

  sortCustomerList = (customerList) => {
    return customerList.sort((a, b) => {
      return a.text.toLowerCase().localeCompare(b.text.toLowerCase());
    });
  };

  getSiteList = async () => {
    const { user, userData } = this.context;
    const siteList = await APIClient.fetchSitesDropDown(user.idToken, userData.selectedCustomer);
    const orderedSiteList = this.sortSiteList(siteList.response);
    this.setState({ siteList: orderedSiteList });
  };

  sortSiteList = (siteList) => {
    // sort list of site options alphabetically
    // always list the "Default" site first in the dropdown
    const orderedSiteList = siteList.sort((a, b) => {
      return a.text.toLowerCase().localeCompare(b.text.toLowerCase());
    });
    const defaultSite = _.remove(orderedSiteList, (o) => o.value === '-1');
    orderedSiteList.unshift(defaultSite[0]);
    return orderedSiteList;
  };

  setSelectedCustomer = async (customerValue) => {
    const { reload, redirect, setRedirectFunction, user, abortController } = this.context;
    if (redirect) {
      this.setState({ selectedCustomer: customerValue }, () => {
        if (abortController) {
          abortController.abort();
        }
        redirect();
        setRedirectFunction(null);
      });
    } else {
      this.setState({ selectedCustomer: customerValue, selectedSite: '-1' }, () => {
        if (abortController) {
          abortController.abort();
        }
        reload();
      });
    }

    this.setSelectedCustomerName(customerValue);

    const siteList = await APIClient.fetchSitesDropDown(user.idToken, customerValue);
    const orderedSiteList = this.sortSiteList(siteList.response);
    this.setState({ siteList: orderedSiteList });
  };

  //Gets the name of selected customer from Drop Down
  setSelectedCustomerName = (customerId) => {
  //Extracting the name of the customer from the customer id and name key-value pair
    var name = this.state.customerList.find(selectedCustomerName => selectedCustomerName.value === customerId);
    this.setState({selectedCustomerName: name.text});
  };



  setSelectedSite = async (siteValue) => {
    const { reload, abortController } = this.context;
    this.setState({ selectedSite: siteValue }, () => {
      if (abortController) {
        abortController.abort();
      }
      reload();
    });
  };

  render() {
    const { children } = this.props;
    const { selectedCustomer, selectedCustomerName, selectedSite, customerList, siteList } = this.state;
    return (
      <CustomerSiteContext.Provider
        value={{
          // state variables
          selectedCustomer,
          selectedCustomerName,
          selectedSite,
          customerList,
          siteList,
          // functions
          init: this.init,
          setSelectedCustomer: this.setSelectedCustomer,
          setSelectedCustomerName: this.setSelectedCustomerName,
          setSelectedSite: this.setSelectedSite,
          getSiteList: this.getSiteList,
        }}
      >
        {children}
      </CustomerSiteContext.Provider>
    );
  }
}

CustomerSiteProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

CustomerSiteProvider.contextType = AppContext;
